'use client';
import {
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import styles from './Tooltip.module.scss';
import QuestionIcon from './icons/QuestionIcon';

interface TooltipProps {
  children: ReactNode;
  subject: string;
  horizontalAlign?: 'left' | 'right';
  verticalAlign?: 'top' | 'bottom';
  color?: 'blue' | 'white' | 'purple';
  flag?: boolean;
  text?: string;
}

export default function Tooltip({
  children,
  subject,
  horizontalAlign = 'left',
  verticalAlign = 'top',
  color = 'purple',
  flag,
  text,
}: TooltipProps): ReactElement {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const contentId = useId();

  const handleClickOutside = (event: MouseEvent): void => {
    if (
      open &&
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (event.key === 'Escape' && open) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const classList: string[] = [styles.tooltip];
  if (open) {
    classList.push(styles.tooltipOpen);
  }
  if (flag) {
    classList.push(styles.flag);
  }

  return (
    <span
      className={classList.join(' ')}
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
    >
      <button
        aria-expanded={open}
        aria-controls={contentId}
        aria-label={`${open ? 'Close' : 'Open'} ${subject} tooltip`}
        className={styles.tooltipBtn}
        onClick={(): void => {
          setOpen(!open);
        }}
        type="button"
      >
        <QuestionIcon color={color} />
        {text}
      </button>
      <span
        id={contentId}
        aria-hidden={!open}
        className={`${styles.tooltipContent} ${styles[horizontalAlign]} ${styles[verticalAlign]}`}
      >
        {children}
      </span>
    </span>
  );
}
